import React, { useState, useEffect } from "react";
import { styled } from "@mui/material/styles";
import Dialog from "@mui/material/Dialog";
import { DialogContent, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteIcon from "@mui/icons-material/Delete";
import Button from "../../../../component-lib/Molecules/button/Button";
import Heading from "../../../../component-lib/Atoms/heading/EventEHRHeading";
import SelectDropdown from "../../../../component-lib/Atoms/Select/Select";
import EventEHRGroupedSelect from "../../../../component-lib/Atoms/Select/EventEHRGroupSelect";
import EventEHRLazyDropdown from "../../../../component-lib/Molecules/lazy-loading-search/EventEHRLazyLoadingSearch";
import { searchIcon } from "../../../../assets";
import { useDispatch, useSelector } from "react-redux";
import { postBundleOrder } from "../../patientSaga";
import {
  hideLoader,
  showLoader,
} from "../../../../component-lib/Molecules/loader/LoaderSlice";
import { getLazyLoadingApiResponse } from "../../../../utils/constantFunctions";
import { getLoginUserData } from "../../../../utils/getToken";
import { FREQUENCY_OPTIONS } from "../constant";
import { componentKey } from "../../patientSlice";
import PatientListService from "../../../../services/PatientListService";
import { fetchAllPages } from "../../../../utils/fetchAllPages";
import { canOrderXRay } from "../../../../utils/careType/canOrderXRay";
import { isXRayName } from "../../../../utils/careType/isXRayName";
import MultipleSelectWithEvents from "../../../../component-lib/Atoms/Select/MultiSelect";

interface OrderTypeOption {
  label: string;
  value: string;
  // typeName is the API `type` string sent to the backend ("Rx Order", "EKG", "X_Ray", ...)
  typeName: string;
  isRx: boolean;
  isXRay: boolean;
  isItemRequired: boolean;
  isNoteRequired: boolean;
}

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: "16px 24px",
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
  "& .MuiPaper-root": {
    width: "1200px", // Set your custom width here
    maxWidth: "none", // Prevents Material-UI from overriding your width
  },
}));

interface OrderItem {
  id: string;
  value: string;
}

interface ConditionRow {
  id: string;
  type: string; // selected order-type value ("rxOrder", "xRayOrder", or an order-type id)
  conditionName: string;
  medicationName: string;
  frequency: string;
  quantity: string;
  items: OrderItem[]; // non-Rx item list (and X-Ray body part as items[0])
  note: string; // non-Rx note
}

const genId = () => `${Date.now()}-${Math.floor(Math.random() * 1e6)}`;

type BundleCondition =
  | {
      conditionName: string;
      type: string;
      medicationName: string;
      facilityInventoryId: string;
      frequency: string;
      quantity: number;
    }
  | { type: string; name?: string; note?: string };

interface MedicationOption {
  label: string;
  value: string;
  facilityInventoryId: string;
  unitOfMeasurement?: string;
}

interface MedicationState {
  options: MedicationOption[];
  search: string;
  pageCount: number;
  resetFlag: boolean;
  selectedValue: MedicationOption | null;
}

interface BundleOrderModalProps {
  readonly open: boolean;
  readonly handleClose: () => void;
  readonly handleSave: (data: {
    conditions: BundleCondition[];
    vitals: Array<{
      vitalName: string;
      facilityInventoryId: string | null;
      frequency: string;
    }>;
  }) => void;
  readonly patientId?: string;
  readonly visitId?: string;
  readonly visitNoteId?: string;
}

// Mock data for dropdowns
const conditionOptions = [
  { label: "Hypertension", value: "hypertension" },
  { label: "Diabetes", value: "diabetes" },
  { label: "Heart Disease", value: "heart_disease" },
  { label: "Asthma", value: "asthma" },
  { label: "Arthritis", value: "arthritis" },
];

const frequencyOptions = FREQUENCY_OPTIONS;

const vitalOptions = [
  { label: "Blood Pressure", value: "Blood Pressure" },
  { label: "Pulse", value: "Pulse" },
  { label: "Heart Rate", value: "Heart Rate" },
  { label: "Respiratory Rate", value: "Respiratory Rate" },
  { label: "Oxygen Saturation", value: "Oxygen Saturation" },
  { label: "Temperature", value: "Temperature" },
];

export default function BundleOrderModal({
  open,
  handleClose,
  handleSave,
}: BundleOrderModalProps) {
  const { visitNoteCareFlowData, notesData } = useSelector(
    (state: any) => state[componentKey]
  );

  let index = visitNoteCareFlowData?.visit_note?.findIndex(
    (obj: any) => obj.id === notesData.id
  );
  // Set index to last  element if not found

  if (index === -1) {
    index = visitNoteCareFlowData?.visit_note?.length - 1;
  }

  const dispatch = useDispatch();

  // Get login user data for facility ID
  const parseLoginUserdata: unknown = getLoginUserData();
  const loginUserdata = JSON.parse(parseLoginUserdata as string);

  const [conditions, setConditions] = useState<ConditionRow[]>([
    {
      id: "1",
      type: "rxOrder",
      conditionName: "",
      medicationName: "",
      frequency: "",
      quantity: "",
      items: [],
      note: "",
    },
  ]);

  // Order types: Rx + the same /order-type list the standard clinic visit uses.
  const RX_ORDER_OPTION: OrderTypeOption = {
    label: "Rx Order",
    value: "rxOrder",
    typeName: "Rx Order",
    isRx: true,
    isXRay: false,
    isItemRequired: false,
    isNoteRequired: false,
  };

  const [orderTypeOptions, setOrderTypeOptions] = useState<OrderTypeOption[]>([
    RX_ORDER_OPTION,
  ]);

  const getOrderTypeOption = (value: string) =>
    orderTypeOptions.find((o) => o.value === value);

  // Vitals are a single "Vital Signs" order: pick one or more vitals to monitor
  // (at least one required) plus one shared frequency.
  const [selectedVitals, setSelectedVitals] = useState<string[]>([]);
  const [vitalsFrequency, setVitalsFrequency] = useState("");

  // Once the user attempts to save, empty required fields show inline errors.
  const [attemptedSave, setAttemptedSave] = useState(false);
  const reqErr = (empty: boolean) => attemptedSave && empty;

  // State for lazy loading medication search - one per row
  const [conditionMedicationStates, setConditionMedicationStates] = useState<{
    [key: string]: MedicationState;
  }>({});

  // Full medication list — loaded once and shared across all condition rows
  // (the inventory is identical per facility).
  const [medicationOptions, setMedicationOptions] = useState<
    MedicationOption[]
  >([]);

  // Load the FULL medication (facility inventory) list once and let the
  // dropdown filter client-side. Mirrors the ICD/DSM "load all" fix; replaces
  // the old per-row limit=6 lazy loading + server search that was slow and
  // showed incomplete data.
  const loadAllMedications = async () => {
    try {
      dispatch(showLoader());
      const facilityId = loginUserdata?.customAttributes?.facilityId;
      const mapRows = (list: any): MedicationOption[] =>
        (Array.isArray(list) ? list : []).map((data: any) => ({
          label: `${data?.inventory?.itemName}`,
          value: `${data?.id}`,
          facilityInventoryId: `${data?.id}`,
          unitOfMeasurement: `${data?.inventory?.unitOfMeasure}`,
        }));
      const fetchPage = (page: number) =>
        getLazyLoadingApiResponse(
          "facility/facility/inventory",
          page,
          100,
          facilityId,
          true
        );

      const first = await fetchPage(1);
      let rows = mapRows(first?.data?.data);
      const totalPages = first?.data?.totalPages ?? 1;

      if (totalPages > 1) {
        const rest = await Promise.all(
          Array.from({ length: totalPages - 1 }, (_, i) =>
            fetchPage(i + 2).then((r) => mapRows(r?.data?.data))
          )
        );
        rows = rows.concat(...rest);
      }

      setMedicationOptions(rows);
    } catch (error) {
      console.log("Medication load error:", error);
    } finally {
      dispatch(hideLoader());
    }
  };

  // Load the medication list once when the modal opens.
  useEffect(() => {
    if (open) {
      loadAllMedications();
    }
  }, [open]);

  // Load order types (Rx + /order-type API) when the modal opens.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        // Fetch ALL order types (page through everything) so the dropdown is
        // never truncated by the page limit. The list is scoped to the current
        // facility by the request interceptor (facilityId on every GET).
        const list = await fetchAllPages(
          PatientListService.getOrderTypes,
          (res) => res?.data?.orderTypes,
        );
        if (cancelled) return;
        const apiTypes: OrderTypeOption[] = list.map((o: any) => {
          const isXRay = isXRayName(o.name);
          return {
            label: o.name,
            value: o.id,
            // Preserve the X_Ray order type string + required Body-Part/Notes
            // behaviour the hard-coded option used to provide.
            typeName: isXRay ? "X_Ray" : o.name,
            isRx: false,
            isXRay,
            isItemRequired: isXRay ? true : !!o.isItemRequired,
            isNoteRequired: isXRay ? true : !!o.isNoteRequired,
          };
        });

        // X-Ray comes from master data (facility-scoped) but is orderable only
        // for Physician Care / Orthopedic Physician Care — hide it otherwise.
        const showXRay = canOrderXRay(visitNoteCareFlowData?.typeOfCare);
        const visibleTypes = apiTypes.filter((o) => !o.isXRay || showXRay);

        setOrderTypeOptions([RX_ORDER_OPTION, ...visibleTypes]);
      } catch (error) {
        console.log("Order type load error:", error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  // Set initial condition name with chief complaint
  useEffect(() => {
    if (open && visitNoteCareFlowData?.chiefComplaints) {
      setConditions(prev => 
        prev?.map((row, index) => 
          index === 0 
            ? { ...row, conditionName: visitNoteCareFlowData?.chiefComplaints }
            : row
        )
      );
    }
  }, [open, visitNoteCareFlowData?.chiefComplaints]);

  // Initialize medication states for existing rows and load initial data
  useEffect(() => {
    const newConditionStates = { ...conditionMedicationStates };

    conditions.forEach((row) => {
      if (!newConditionStates[row.id]) {
        newConditionStates[row.id] = {
          options: [],
          search: "",
          pageCount: 1,
          resetFlag: false,
          selectedValue: null,
        };
      }
    });

    setConditionMedicationStates(newConditionStates);
  }, [conditions.length, open]);

  // Effect to sync medication selections with row data
  useEffect(() => {
    Object.keys(conditionMedicationStates).forEach((rowId) => {
      const state = conditionMedicationStates[rowId];
      if (state.selectedValue) {
        const updatedConditions = conditions
          .map((row) =>
            row && row.id === rowId
              ? { ...row, medicationName: state.selectedValue?.label || "" }
              : row
          )
          .filter(Boolean);
        setConditions(updatedConditions);
      }
    });
  }, [conditionMedicationStates]);

  // Helper functions to update medication states for specific rows
  const updateConditionMedicationState = (
    rowId: string,
    updates: Partial<MedicationState>
  ) => {
    setConditionMedicationStates((prev) => ({
      ...prev,
      [rowId]: {
        ...prev[rowId],
        ...updates,
      },
    }));
  };

  const handleCloseDialog = () => {
    handleClose();
  };

  const handleBackClick = () => {
    handleClose();
  };

  const addConditionRow = () => {
    const newRow: ConditionRow = {
      id: Date.now().toString(),
      type: "rxOrder",
      conditionName: "",
      medicationName: "",
      frequency: "",
      quantity: "",
      items: [],
      note: "",
    };
    setConditions([...conditions, newRow]);
  };

  // Change a row's order type; seed one empty item for item-based types.
  const changeConditionType = (id: string, type: string) => {
    const option = orderTypeOptions.find((o) => o.value === type);
    const needsItem = !!option && !option.isRx && option.isItemRequired;
    setConditions(
      conditions.map((row) =>
        row.id === id
          ? {
              ...row,
              type,
              items:
                needsItem && row.items.length === 0
                  ? [{ id: genId(), value: "" }]
                  : row.items,
            }
          : row
      )
    );
  };

  const updateItemInRow = (rowId: string, itemId: string, value: string) => {
    setConditions(
      conditions.map((row) =>
        row.id === rowId
          ? {
              ...row,
              items: row.items.map((it) =>
                it.id === itemId ? { ...it, value } : it
              ),
            }
          : row
      )
    );
  };

  const updateConditionRow = (
    id: string,
    field: keyof ConditionRow,
    value: string
  ) => {
    setConditions(
      conditions.map((row) =>
        row.id === id ? { ...row, [field]: value } : row
      )
    );
  };

  const deleteConditionRow = (id: string) => {
    if (conditions.length > 1) {
      setConditions(conditions.filter((row) => row.id !== id));
    }
  };

  const handleSaveClick = () => {
    // Validate every required field: all condition rows must be complete and the
    // vitals must have at least one vital + a frequency. Empty required fields
    // show inline errors (via attemptedSave); don't submit if anything is empty.
    setAttemptedSave(true);
    const hasIncompleteCondition = conditions.some(isConditionRowIncomplete);
    const hasInvalidVitals = selectedVitals.length === 0 || !vitalsFrequency;
    if (hasIncompleteCondition || hasInvalidVitals) {
      return;
    }

    const getFrequencyMapping = (frequencyValue: string) => {
      return frequencyValue;
    };

    const transformedConditions = conditions
      ?.flatMap((row): BundleCondition[] => {
        const option = getOrderTypeOption(row.type);

        // Rx Order: medication + inventory + frequency + quantity
        if (!option || option.isRx) {
          const selectedMedication =
            conditionMedicationStates[row.id]?.selectedValue;
          const facilityInventoryId =
            selectedMedication?.facilityInventoryId || "";
          if (
            !row.conditionName ||
            !facilityInventoryId ||
            !row.frequency ||
            !row.quantity
          ) {
            return [];
          }
          return [
            {
              conditionName: row.conditionName,
              type: "Rx Order",
              medicationName:
                selectedMedication?.label || row.medicationName || "",
              facilityInventoryId,
              frequency: getFrequencyMapping(row.frequency),
              quantity: row.quantity ? parseInt(row.quantity) : 0,
            },
          ];
        }

        // Non-Rx / X-Ray: each item becomes its own condition (shared note).
        const note = row.note?.trim() || undefined;
        const itemValues = row.items
          .map((it) => it.value.trim())
          .filter(Boolean);

        if (itemValues.length > 0) {
          return itemValues.map((name) => ({
            type: option.typeName,
            name,
            note,
          }));
        }

        // Verbal / note-only order: no items, just a note.
        if (note) {
          return [{ type: option.typeName, note }];
        }
        // No-field type (neither item nor note required): just the type.
        if (!option.isItemRequired && !option.isNoteRequired) {
          return [{ type: option.typeName }];
        }
        return [];
      });

    // One "Vital Signs" order: every selected vital shares the single frequency.
    const transformedVitals = selectedVitals.map((vitalName) => ({
      vitalName,
      facilityInventoryId: null, // Vitals don't have medication selection
      frequency: getFrequencyMapping(vitalsFrequency),
    }));

    const bundleOrderPayload = {
      visitId: visitNoteCareFlowData?.id,
      patientId: visitNoteCareFlowData?.patientId,
      visitNoteId: visitNoteCareFlowData?.visit_note?.[index]?.id,
      conditions: transformedConditions,
      vitals: transformedVitals,
      onSuccess: (responseData: unknown) => {
        console.log("Bundle order created successfully:", responseData);
        handleSave({
          conditions: transformedConditions,
          vitals: transformedVitals,
        });
        handleClose();
      },
      onError: (error: Error) => {
        console.error("Bundle order creation failed:", error);
      },
    };

    // Dispatch the saga action
    dispatch(postBundleOrder(bundleOrderPayload));
  };

  const sectionHeadingStyle = {
    color: "#004981",
    fontSize: "16px",
    fontWeight: 600,
    marginBottom: "16px",
  };

  const subSectionHeadingStyle = {
    color: "#000000",
    fontSize: "14px",
    fontWeight: 600,
    marginBottom: "8px",
  };

  // Grouped Order Type options: Rx Order, then a "Non RX Order" subheader + types.
  const nonRxOrderTypeOptions = orderTypeOptions.filter((o) => !o.isRx);
  const groupedOrderTypeOptions = [
    { type: "item", label: "Rx Order", value: "rxOrder" },
    ...(nonRxOrderTypeOptions.length
      ? [{ type: "subheader", label: "Non RX Order" }]
      : []),
    ...nonRxOrderTypeOptions.map((o) => ({
      type: "item",
      label: o.label,
      value: o.value,
    })),
  ];

  const plainInputStyle: React.CSSProperties = {
    width: "100%",
    padding: "8px 12px",
    border: "1px solid #D2D2D2",
    borderRadius: "4px",
    fontSize: "14px",
    backgroundColor: "white",
    color: "#000000",
    outline: "none",
  };

  const errorTextStyle: React.CSSProperties = {
    color: "#d32f2f",
    fontSize: "12px",
    marginTop: "4px",
  };

  // Border for a raw <input> that shows a required-field error inline.
  const inputBorder = (empty: boolean) =>
    reqErr(empty) ? "1px solid #d32f2f" : "1px solid #D2D2D2";

  const isConditionRowIncomplete = (row: ConditionRow) => {
    const option = getOrderTypeOption(row.type);
    const isRx = !option || option.isRx;
    if (isRx) {
      return (
        !row.conditionName ||
        !row.medicationName ||
        !row.frequency ||
        !row.quantity
      );
    }
    // Non-Rx: only the fields flagged by the order type are required. A type
    // that requires neither item nor note is complete on selection alone.
    const hasItem = row.items.some((it) => it.value.trim());
    if (option?.isItemRequired && !hasItem) return true;
    if (option?.isNoteRequired && !row.note?.trim()) return true;
    return false;
  };

  const isAddMoreConditionsDisabled = conditions?.some(
    isConditionRowIncomplete
  );

  return (
    <BootstrapDialog open={open} aria-labelledby="bundle-order-dialog-title">
      <div className="h-full flex flex-col justify-between">
        {/* Header with Back arrow */}
        <div
          className="flex justify-between items-center rounded-lg"
          style={{ margin: "14px 10px" }}
        >
          <div className="flex items-center">
            <IconButton
              aria-label="back"
              onClick={handleBackClick}
              sx={{
                color: "#565656",
                padding: "6px",
                marginLeft: "0px",
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Heading title="Bundle Order" />
          </div>
          <IconButton
            aria-label="close"
            onClick={handleCloseDialog}
            sx={{
              color: "#565656",
            }}
          >
            <CloseIcon />
          </IconButton>
        </div>

        <hr />

        {/* MAIN CONTENT */}
        <DialogContent
          sx={{
            height: "fit-content",
            maxHeight: "65vh",
            overflow: "auto",
          }}
        >
          <div className="flex flex-col gap-8">
            {/* CONDITIONS SECTION */}
            <div>
              <Heading title="Conditions" customStyle={sectionHeadingStyle} />

              {conditions.map((row, index) => {
                const option = getOrderTypeOption(row.type);
                const isRx = !option || option.isRx;
                return (
                <div
                  key={row.id}
                  className={`flex gap-4 items-end ${index > 0 ? "mt-4" : ""}`}
                >
                  <div className="flex-1">
                    <Heading
                      title="Order Type"
                      customStyle={subSectionHeadingStyle}
                    />
                    <EventEHRGroupedSelect
                      label=""
                      id={`orderType_${row.id}`}
                      defaultValue={row.type}
                      options={groupedOrderTypeOptions}
                      onChange={(e) =>
                        changeConditionType(row.id, e.target.value)
                      }
                    />
                  </div>

                  {isRx && (
                    <>
                  <div className="flex-1">
                    <Heading
                      title="Condition Name"
                      customStyle={subSectionHeadingStyle}
                    />
                    <input
                      type="text"
                      value={row.conditionName}
                      placeholder="Condition Name"
                      disabled={row.id === "1"}
                      onChange={(e) =>
                        updateConditionRow(
                          row.id,
                          "conditionName",
                          e.target.value
                        )
                      }
                      style={{
                        width: "100%",
                        padding: "8px 12px",
                        border: reqErr(!row.conditionName)
                          ? "1px solid #d32f2f"
                          : "1px solid #D2D2D2",
                        borderRadius: "4px",
                        fontSize: "14px",
                        backgroundColor: row.id === "1" ? "#F8F8F8" : "white",
                        color: row.id === "1" ? "#979797" : "#000000",
                        outline: "none",
                        transition: "border-color 0.2s ease-in-out",
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = "#1976d2";
                        e.target.style.borderWidth = "2px";
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = reqErr(!row.conditionName)
                          ? "#d32f2f"
                          : "#D2D2D2";
                        e.target.style.borderWidth = "1px";
                      }}
                    />
                    {reqErr(!row.conditionName) && (
                      <p style={errorTextStyle}>Required</p>
                    )}
                  </div>

                  <div className="flex-1">
                    <Heading
                      title="Medication Name"
                      customStyle={subSectionHeadingStyle}
                    />
                    <EventEHRLazyDropdown
                      dropdownOptions={medicationOptions}
                      selectedValue={
                        conditionMedicationStates[row.id]?.selectedValue || null
                      }
                      error={reqErr(
                        !conditionMedicationStates[row.id]?.selectedValue
                      )}
                      helperText={
                        reqErr(!conditionMedicationStates[row.id]?.selectedValue)
                          ? "Required"
                          : ""
                      }
                      placeholder="Search Medication Name"
                      // label="Search Medication Name"
                      startIcon={
                        <img
                          src={searchIcon}
                          alt="search"
                          width="16"
                          height="16"
                        />
                      }
                      dropdownIcon={true}
                      dropdownSearch={
                        conditionMedicationStates[row.id]?.search || ""
                      }
                      noOptionText={
                        medicationOptions.length === 0
                          ? "Loading..."
                          : "No options found"
                      }
                      setResetFlag={(resetFlag) => {
                        updateConditionMedicationState(row.id, { resetFlag });
                      }}
                      setPageCount={(pageCount) => {
                        updateConditionMedicationState(row.id, { pageCount });
                      }}
                      setSelectedValue={(selectedValue) =>
                        updateConditionMedicationState(row.id, {
                          selectedValue,
                        })
                      }
                      setDropdownSearch={(search) => {
                        updateConditionMedicationState(row.id, { search });
                      }}
                    />
                  </div>

                  <div className="flex-1">
                    <Heading
                      title="Frequency"
                      customStyle={subSectionHeadingStyle}
                    />
                    <SelectDropdown
                      label="Frequency"
                      name={`frequency_${row.id}`}
                      value={row.frequency}
                      placeholderValue="Select"
                      options={frequencyOptions}
                      error={reqErr(!row.frequency)}
                      helperText={reqErr(!row.frequency) ? "Required" : ""}
                      onChangeCb={(e) =>
                        updateConditionRow(row.id, "frequency", e.target.value)
                      }
                    />
                  </div>

                  <div className="flex-1">
                    <Heading
                      title="Quantity"
                      customStyle={subSectionHeadingStyle}
                    />
                    <input
                      type="number"
                      value={row.quantity}
                      placeholder="Enter Quantity"
                      onChange={(e) =>
                        updateConditionRow(
                          row.id,
                          "quantity",
                          e.target.value
                        )
                      }
                      style={{
                        width: "100%",
                        padding: "8px 12px",
                        border: inputBorder(!row.quantity),
                        borderRadius: "4px",
                        fontSize: "14px",
                        backgroundColor: "white",
                        color: "#000000",
                        outline: "none",
                        transition: "border-color 0.2s ease-in-out",
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = "#1976d2";
                        e.target.style.borderWidth = "2px";
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = reqErr(!row.quantity)
                          ? "#d32f2f"
                          : "#D2D2D2";
                        e.target.style.borderWidth = "1px";
                      }}
                    />
                    {reqErr(!row.quantity) && (
                      <p style={errorTextStyle}>Required</p>
                    )}
                  </div>
                    </>
                  )}

                  {/* X-Ray: single body part + notes (mirrors Create Order) */}
                  {!isRx && option?.isXRay && (
                    <>
                      <div className="flex-1">
                        <Heading
                          title="Body Part"
                          customStyle={subSectionHeadingStyle}
                        />
                        <input
                          type="text"
                          value={row.items[0]?.value || ""}
                          placeholder="Enter Body Part"
                          onChange={(e) => {
                            const itemId = row.items[0]?.id;
                            if (itemId) {
                              updateItemInRow(row.id, itemId, e.target.value);
                            }
                          }}
                          style={{
                            ...plainInputStyle,
                            border: inputBorder(!row.items[0]?.value?.trim()),
                          }}
                        />
                        {reqErr(!row.items[0]?.value?.trim()) && (
                          <p style={errorTextStyle}>Required</p>
                        )}
                      </div>
                      <div className="flex-1">
                        <Heading
                          title="Notes"
                          customStyle={subSectionHeadingStyle}
                        />
                        <input
                          type="text"
                          value={row.note}
                          placeholder="Enter Notes"
                          onChange={(e) =>
                            updateConditionRow(row.id, "note", e.target.value)
                          }
                          style={{
                            ...plainInputStyle,
                            border: inputBorder(!row.note?.trim()),
                          }}
                        />
                        {reqErr(!row.note?.trim()) && (
                          <p style={errorTextStyle}>Required</p>
                        )}
                      </div>
                    </>
                  )}

                  {/* Non-Rx item types: a single item (use "+ Add More" for more) */}
                  {!isRx && !option?.isXRay && option?.isItemRequired && (
                    <div className="flex-1">
                      <Heading
                        title="Item"
                        customStyle={subSectionHeadingStyle}
                      />
                      <input
                        type="text"
                        value={row.items[0]?.value || ""}
                        placeholder="Enter Item"
                        onChange={(e) => {
                          const itemId = row.items[0]?.id;
                          if (itemId) {
                            updateItemInRow(row.id, itemId, e.target.value);
                          }
                        }}
                        style={{
                          ...plainInputStyle,
                          border: inputBorder(!row.items[0]?.value?.trim()),
                        }}
                      />
                      {reqErr(!row.items[0]?.value?.trim()) && (
                        <p style={errorTextStyle}>Required</p>
                      )}
                    </div>
                  )}

                  {/* Notes for non-Rx types that require a note (incl. verbal) */}
                  {!isRx && !option?.isXRay && option?.isNoteRequired && (
                    <div className="flex-1">
                      <Heading
                        title="Notes"
                        customStyle={subSectionHeadingStyle}
                      />
                      <input
                        type="text"
                        value={row.note}
                        placeholder="Enter Notes"
                        onChange={(e) =>
                          updateConditionRow(row.id, "note", e.target.value)
                        }
                        style={{
                          ...plainInputStyle,
                          border: inputBorder(!row.note?.trim()),
                        }}
                      />
                      {reqErr(!row.note?.trim()) && (
                        <p style={errorTextStyle}>Required</p>
                      )}
                    </div>
                  )}

                  <div className="flex items-end pb-2">
                    <IconButton
                      aria-label="delete condition"
                      onClick={() => deleteConditionRow(row.id)}
                      disabled={conditions.length === 1}
                      sx={{
                        color: conditions.length === 1 ? "#ccc" : "#ff4444",
                        padding: "8px",
                        "&:hover": {
                          backgroundColor:
                            conditions.length === 1
                              ? "transparent"
                              : "rgba(255, 68, 68, 0.1)",
                        },
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </div>
                </div>
                );
              })}

              <div className="mt-4 text-end">
                <Button
                  type="button"
                  label="+ Add More"
                  variant="outlined"
                  disabled={isAddMoreConditionsDisabled}
                  onClickCb={addConditionRow}
                  style={{
                    padding: "8px 16px",
                    fontSize: "14px",
                    borderColor: isAddMoreConditionsDisabled
                      ? "#ccc"
                      : "#004981",
                    color: isAddMoreConditionsDisabled ? "#ccc" : "#004981",
                    backgroundColor: "white",
                    whiteSpace: "nowrap",
                  }}
                />
              </div>
            </div>

            {/* VITALS SECTION — a single "Vital Signs" order: pick one or more
                vitals (at least one required) + one shared frequency. */}
            <div>
              <hr style={{ marginBottom: "16px" }} />
              <Heading title="Vital Signs" customStyle={sectionHeadingStyle} />

              <div className="flex gap-4 items-end">
                <div className="flex-1">
                  <Heading
                    title="Vital Signs"
                    customStyle={subSectionHeadingStyle}
                  />
                  <MultipleSelectWithEvents
                    label="Select Vital Signs"
                    options={vitalOptions}
                    renderAsChips
                    defaultSelectedValues={selectedVitals}
                    error={reqErr(selectedVitals.length === 0)}
                    helperText={
                      reqErr(selectedVitals.length === 0) ? "Required" : ""
                    }
                    onSelectionChanged={(values) => setSelectedVitals(values)}
                  />
                </div>

                <div className="flex-1">
                  <Heading
                    title="Frequency"
                    customStyle={subSectionHeadingStyle}
                  />
                  <SelectDropdown
                    label="Frequency"
                    name="vitals_frequency"
                    value={vitalsFrequency}
                    placeholderValue="Select"
                    options={frequencyOptions}
                    error={reqErr(!vitalsFrequency)}
                    helperText={reqErr(!vitalsFrequency) ? "Required" : ""}
                    onChangeCb={(e) => setVitalsFrequency(e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>
        </DialogContent>

        {/* SAVE BUTTON */}
        <div className="mt-6 mb-4 text-end px-6">
          <Button
            type="button"
            label="Save"
            variant="contained"
            onClickCb={handleSaveClick}
            style={{
              padding: "8px 32px",
              fontSize: "16px",
              backgroundColor: "#004981",
              borderColor: "#004981",
            }}
          />
        </div>
      </div>
    </BootstrapDialog>
  );
}
